import * as CL from '@design-system/component-library';
import { FormProvider, useController, useWatch } from 'react-hook-form';
import { useDeviceSelectionFormCoCo } from '../../common/hooks/useDeviceSelectionFormCoCo.js';
import { closeMsg, descriptionMsg, productTypeMsg, t } from '../../common/i18n/index.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { TextAreaComponent } from '../../common/react-hook-form/components/TextAreaComponent.js';
import { TextInput } from '../../common/react-hook-form/fields/TextInput.js';
import { getDeviceSelectionProductTypeLabel } from '../../common/utils/deviceListTabsUtilsCoCo.js';
import { getSelectedDeviceCategoryCounts } from '../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import {
  DEFAULT_AVAILABLE_PRODUCT_TYPES,
  DESCRIPTION_MAX_LENGTH,
  DeviceSelectionFormFieldCoCo,
} from '../../common/utils/deviceSelectionFormUtilsCoCo.js';
import { DeviceSelectionCategoriesCoCo } from './components/DeviceSelectionCategoriesCoCo.js';
import { DeviceSelectionDeviceListCoCo } from './components/DeviceSelectionDeviceListCoCo.js';
import { DeviceSelectionFooterCoCo } from './components/DeviceSelectionFooterCoCo.js';
import type { DeviceSelectionProductTypeCoCo } from '../../common/utils/deviceListTabsUtilsCoCo.js';
import type {
  DeviceSelectionDeviceCoCo,
  DeviceSelectionDevicesByProductTypeCoCo,
} from '../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import type { DeviceSelectionFormValuesCoCo } from '../../common/utils/deviceSelectionFormUtilsCoCo.js';
import type { EmployeeProductSelectionItemResponse } from '../../generated/api/employeeProductSelectionItemResponse.js';

import './DeviceSelectionFormCoCo.scss';

// Watches only the fields it needs, so that e.g. (de)selecting devices doesn't re-render the whole form
const DeviceSelectionProductsCoCo = ({ devices }: { devices: DeviceSelectionDeviceCoCo[] }) => {
  const [productType, category] = useWatch<DeviceSelectionFormValuesCoCo>({
    name: [DeviceSelectionFormFieldCoCo.PRODUCT_TYPE, DeviceSelectionFormFieldCoCo.CATEGORY],
  }) as [DeviceSelectionFormValuesCoCo['productType'], DeviceSelectionFormValuesCoCo['category']];

  return (
    <>
      <DeviceSelectionCategoriesCoCo name={DeviceSelectionFormFieldCoCo.CATEGORY} productType={productType} />
      <DeviceSelectionDeviceListCoCo
        key={productType}
        name={DeviceSelectionFormFieldCoCo.SELECTED_DEVICE_IDS}
        category={category}
        devices={devices}
      />
    </>
  );
};

const DeviceSelectionFormFooterCoCo = ({ devices }: { devices: DeviceSelectionDeviceCoCo[] }) => {
  const [productType, selectedDeviceIds] = useWatch<DeviceSelectionFormValuesCoCo>({
    name: [DeviceSelectionFormFieldCoCo.PRODUCT_TYPE, DeviceSelectionFormFieldCoCo.SELECTED_DEVICE_IDS],
  }) as [DeviceSelectionFormValuesCoCo['productType'], DeviceSelectionFormValuesCoCo['selectedDeviceIds']];
  const categoryCounts = getSelectedDeviceCategoryCounts(selectedDeviceIds, productType, devices);

  return <DeviceSelectionFooterCoCo categoryCounts={categoryCounts} total={selectedDeviceIds.length} />;
};

const UnresolvedDevices = ({ items }: { items: EmployeeProductSelectionItemResponse[] }) => {
  const { field } = useController<DeviceSelectionFormValuesCoCo, 'selectedDeviceIds'>({ name: 'selectedDeviceIds' });
  return items.length > 0 ? (
    <section>
      <h3>{t.O9AW('Devices whose category is unavailable')}</h3>
      {items.map(item => {
        const checked = field.value.includes(item.variantGroupId);
        return (
          <CL.Checkbox
            key={`${item.variantGroupId}-${checked}`}
            name={`unresolved-${item.variantGroupId}`}
            checked={checked}
            label={item.variantGroupName}
            onChange={() =>
              field.onChange(
                checked ? field.value.filter(id => id !== item.variantGroupId) : [...field.value, item.variantGroupId]
              )
            }
          />
        );
      })}
    </section>
  ) : null;
};

export interface DeviceSelectionFormCoCoProps {
  // Product types the company is allowed to create device selections for, the first one is selected by default
  availableProductTypes?: DeviceSelectionProductTypeCoCo[];
  initialValues?: DeviceSelectionFormValuesCoCo;
  onValidSubmit?: (values: DeviceSelectionFormValuesCoCo) => void;
  devicesByProductType: DeviceSelectionDevicesByProductTypeCoCo;
  unresolvedItems?: EmployeeProductSelectionItemResponse[];
  lockedProductType?: DeviceSelectionProductTypeCoCo;
}

export const DeviceSelectionFormCoCo = ({
  availableProductTypes = DEFAULT_AVAILABLE_PRODUCT_TYPES,
  initialValues,
  onValidSubmit,
  devicesByProductType,
  unresolvedItems = [],
  lockedProductType,
}: DeviceSelectionFormCoCoProps) => {
  const { devices, methods, onProductTypeChange, onSubmit, productTypeItems } = useDeviceSelectionFormCoCo(
    availableProductTypes,
    devicesByProductType,
    onValidSubmit,
    initialValues,
    lockedProductType
  );

  return (
    <div className="of-device-selection-form-coco">
      <CL.Disclaimer
        className="of-device-selection-form-coco__disclaimer"
        disclaimerType="info"
        icon={<CL.Icon icon="information" type="regular" aria-hidden="true" />}
        showCloseButton
        i18n_disclaimer_closeButtonTitle={t.WOYD(closeMsg)}
        ariaRole="status"
        ariaLive="polite"
        visible
        text={t.FAOI(
          'Select which devices and accessories employees can order on Device lists that use this Device selection.'
        )}
      />
      <FormProvider {...methods}>
        <form className="of-device-selection-form-coco__form" noValidate onSubmit={onSubmit}>
          <TextInput name={DeviceSelectionFormFieldCoCo.NAME} label={t.VJ0T('Name of device selection')} required />
          <TextAreaComponent
            name={DeviceSelectionFormFieldCoCo.DESCRIPTION}
            label={t.HBW3(descriptionMsg)}
            maxLength={DESCRIPTION_MAX_LENGTH}
            hint={t.NPBA('Max {} characters', String(DESCRIPTION_MAX_LENGTH))}
            disabled={false}
            required
          />
          <SelectRadio
            name={DeviceSelectionFormFieldCoCo.PRODUCT_TYPE}
            label={t.R6HP(productTypeMsg)}
            required
            items={
              lockedProductType
                ? DEFAULT_AVAILABLE_PRODUCT_TYPES.map(productType => ({
                    label: getDeviceSelectionProductTypeLabel(productType),
                    value: productType,
                    disabled: productType !== lockedProductType,
                  }))
                : productTypeItems
            }
            onChange={value => onProductTypeChange(value as DeviceSelectionProductTypeCoCo)}
          />
          <DeviceSelectionProductsCoCo devices={devices} />
          <UnresolvedDevices items={unresolvedItems} />
          <DeviceSelectionFormFooterCoCo devices={devices} />
        </form>
      </FormProvider>
    </div>
  );
};
